import React, { memo, useCallback, useEffect, useState } from 'react';
import { useAISMapContext } from '../AISMapContext';
import { useFetchShipByMmsi, useGetShipByImoFunction } from '@ais-ui/api';
import { TargetTypeEnum } from '@ais-ui/websockets';
import { InfoIconTooltip, LoadingSpinner, useToast } from '@ais-ui/ui-core';
import {
  capitalize,
  cn,
  isValidIMONumber,
  isValidMMSINumber,
} from '@ais-ui/utils';
import { PiBoatThin } from 'react-icons/pi';
import { useLocalization } from '@ais-ui/locale-management';
import { VesselResult } from '@ais-ui/api-spec/shipdb';
import { useSelectionState } from '../../../components/SelectionStateProvider';
import { usePermissions } from '../../Permissions/PermissionsProvider';
import { useAuth } from '@ais-ui/auth';

interface SearchExtendedVesselInfoProps {
  searchString: string;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ExtendedVesselInfoResultProps {
  isLoading: boolean;
  extendedVesselInfo: VesselResult | null;
  onSelect: () => void;
}

const ExtendedVesselInfoResult = ({
  isLoading,
  extendedVesselInfo,
  onSelect,
}: ExtendedVesselInfoResultProps) => {
  if (isLoading) {
    return <LoadingSpinner size="small"></LoadingSpinner>;
  }

  if (!extendedVesselInfo) {
    return null;
  }

  return (
    <div
      className="flex justify-between items-center hover:cursor-pointer mb-4 h-full"
      onSelect={onSelect}
    >
      <button
        type="button"
        onClick={() => onSelect()}
        className="mr-4 hover:cursor-pointer rounded-lg text-sm w-full px-2 py-1.5 font-normal text-left bg-transparent hover:bg-secondary transition-colors"
      >
        <div className="flex flex-col space-y-1 w-full">
          <div className="flex justify-between">
            <div className="flex items-center space-x-1">
              <PiBoatThin className={cn('mr-2 h-4 w-4')} />
              {extendedVesselInfo?.basic?.vesselName
                ? capitalize(extendedVesselInfo.basic.vesselName)
                : 'Unknown name'}
            </div>
          </div>
          <div className="flex space-x-2 text-xs text-muted-foreground items-center">
            <div>
              MMSI:
              {extendedVesselInfo?.basic?.vesselMmsiNumber
                ? extendedVesselInfo.basic.vesselMmsiNumber
                : ' None'}
            </div>
            <div className={cn('rounded-full h-1 w-1 bg-gray-500')}></div>
            <div>
              IMO:{' '}
              {extendedVesselInfo?.basic?.vesselImoNumber
                ? extendedVesselInfo.basic.vesselImoNumber
                : ' None'}
            </div>
          </div>
        </div>
      </button>
    </div>
  );
};

export const SearchExtendedVesselInfo = memo(
  ({ searchString, setIsFocused }: SearchExtendedVesselInfoProps) => {
    const { setTargetInfoDetailsList } = useSelectionState();
    const { toast } = useToast();
    const { messages } = useLocalization();
    const { getShipByImo } = useGetShipByImoFunction();
    const fetchShipByMmsi = useFetchShipByMmsi();
    const permissions = usePermissions();

    const [isLoading, setIsLoading] = useState(false);
    const [extendedVesselInfo, setExtendedVesselInfo] =
      useState<VesselResult | null>(null);

    useEffect(() => {
      const shipByImo = async (stringImo: string) => {
        setIsLoading(true);
        try {
          const parsedImo = parseInt(stringImo);
          const shipByImo = await getShipByImo(parsedImo);
          setExtendedVesselInfo(shipByImo);
        } catch (error) {
          console.error('Error fetching ship by IMO:', error);
          setExtendedVesselInfo(null);
        } finally {
          setIsLoading(false);
        }
      };

      const shipByMmsi = async (stringMmsi: string) => {
        setIsLoading(true);
        try {
          const parsedMmsi = parseInt(stringMmsi);
          const shipByMmsi = await fetchShipByMmsi(parsedMmsi);
          setExtendedVesselInfo(shipByMmsi);
        } catch (error) {
          console.error('Error fetching ship by MMSI:', error);
          setExtendedVesselInfo(null);
        } finally {
          setIsLoading(false);
        }
      };

      // Do not search if permission is missing
      if (!permissions.mdmAdvancedVesselInfo) {
        return;
      }

      if (isValidIMONumber(searchString)) {
        shipByImo(searchString);
      } else if (isValidMMSINumber(searchString)) {
        shipByMmsi(searchString);
      } else {
        setExtendedVesselInfo(null);
        setIsLoading(false);
      }
      // If fetchShipByMmsi or getShipByImo is added, it runs continuously, which we don't want
    }, [permissions, searchString]);

    const onSelectExtendedVesselInfo = useCallback(() => {
      if (!extendedVesselInfo) {
        return;
      }

      setTargetInfoDetailsList([
        {
          extractedTargetInfo: [extendedVesselInfo],
          extractedTargetInfoType: TargetTypeEnum.ExtendedVessel,
        },
      ]);
      setIsFocused(false);
    }, [extendedVesselInfo, setTargetInfoDetailsList, setIsFocused]);

    return (
      <div className="w-full h-fit">
        <div className="flex justify-between px-2 py-3">
          <p className="px-1 text-xs font-medium text-muted-foreground">
            Extended Vessel Info
          </p>
          <InfoIconTooltip>
            Enter a valid IMO or MMSI to search in Extended Vessel Info
          </InfoIconTooltip>
        </div>
        <ExtendedVesselInfoResult
          isLoading={isLoading}
          extendedVesselInfo={extendedVesselInfo}
          onSelect={onSelectExtendedVesselInfo}
        ></ExtendedVesselInfoResult>
      </div>
    );
  }
);

// Add display name for debugging
SearchExtendedVesselInfo.displayName = 'SearchExtendedVesselInfo';
